'use client';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { BaseLineLeft } from '@/app/components/icons/BaseLineLeft';
import { BaseLineRight } from '@/app/components/icons/BaseLineRight';

interface SkillLinkItem {
  href: string;
  image: string;
  titleKey: string;
}

const SKILL_LINKS: SkillLinkItem[] = [
  {
    href: 'https://www.vidu.com/tools/ai-avatar-video-skills',
    image: 'https://image01.cf.vidu.studio/vidu/media-asset/AI%20Avatar%20Video-b5b860e8.webp',
    titleKey: 'skills.aiAvatar.title',
  },
  {
    href: 'https://www.vidu.com/tools/faceless-video-workflow-skills',
    image: 'https://image01.cf.vidu.studio/vidu/media-asset/Faceless%20Video-e5089944.webp',
    titleKey: 'skills.faceless.title',
  },
  {
    href: 'https://www.vidu.com/tools/social-video-automation-skills',
    image: 'https://image01.cf.vidu.studio/vidu/media-asset/Social%20Video%20Automation-4c834ead.webp',
    titleKey: 'skills.socialAutomation.title',
  },
  {
    href: 'https://www.vidu.com/tools/script-to-video-skills',
    image: 'https://image01.cf.vidu.studio/vidu/media-asset/script%20to%20video-1-5cea0d65.webp',
    titleKey: 'skills.scriptToVideo.title',
  },
  {
    href: 'https://www.vidu.com/tools/text-to-video-skills',
    image: 'https://image01.cf.vidu.studio/vidu/media-asset/Text%20to%20Video-72844b82.webp',
    titleKey: 'skills.textToVideo.title',
  },
];

const PAGE_SIZE = 4;

export function DiscoverMoreSkills() {
  const t = useTranslations('viduClaw');
  const tSection = useTranslations('relatedProducts');
  const [page, setPage] = useState(0);
  const totalPages = Math.ceil(SKILL_LINKS.length / PAGE_SIZE);
  const visible = SKILL_LINKS.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  return (
    <section className="w-full bg-black pt-16">
      <div className="mx-auto max-w-[1440px] px-8 py-12 text-center md:px-4 md:py-8">
        <h2 className="mb-10 w-full font-bold text-[40px] leading-[1.4] md:mb-8 md:font-semibold md:text-[32px]">
          {t('discoverMore.title')}
        </h2>
        <div className="relative flex items-center gap-4">
          {totalPages > 1 && (
            <button
              type="button"
              aria-label={tSection('prev')}
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-white/20 text-white text-xl transition-colors hover:bg-white/10 disabled:opacity-30 sm:hidden"
            >
              <BaseLineLeft />
            </button>
          )}
          <div className="grid flex-1 grid-cols-4 gap-6 sm:grid-cols-2 sm:gap-4">
            {Array.from({ length: PAGE_SIZE }, (_, i) => visible[i] ?? null).map((item, i) => {
              if (!item) return <div key={`placeholder-p${page}-s${PAGE_SIZE - i}`} className="aspect-square" />;
              return (
                <a
                  key={item.href}
                  href={item.href}
                  className="group relative aspect-square overflow-hidden rounded-2xl border border-white/10"
                >
                  <img alt={t(item.titleKey)} className="h-full w-full object-cover" loading="lazy" src={item.image} />
                  <div className="absolute inset-x-0 bottom-0 flex items-center gap-2 bg-[#484848] p-4 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                    <p className="min-w-0 flex-1 font-medium text-[15px] text-white leading-[1.2]">
                      {t(item.titleKey)}
                    </p>
                  </div>
                </a>
              );
            })}
          </div>
          {totalPages > 1 && (
            <button
              type="button"
              aria-label={tSection('next')}
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-white/20 text-white text-xl transition-colors hover:bg-white/10 disabled:opacity-30 sm:hidden"
            >
              <BaseLineRight />
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
